import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Truck, ShieldCheck, HeartPulse, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { name: "Diagnostics", img: "https://images.unsplash.com/photo-1584362917165-526a968579e8?w=600" },
  { name: "First Aid", img: "https://images.unsplash.com/photo-1603398938378-e54eab446dde?w=600" },
  { name: "PPE", img: "https://images.unsplash.com/photo-1584744646333-f78e8b28de55?w=600" },
  { name: "Mobility", img: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=600" },
  { name: "Wellness", img: "https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?w=600" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products?featured=true`).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container-app px-6 md:px-10 pt-14 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span data-testid="hero-eyebrow" className="inline-block px-3 py-1 rounded-full bg-[#E8EDE9] text-[#2C4035] text-xs font-semibold tracking-wide uppercase">
              Trusted since 2018
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1A1A] leading-[1.05]">
              Medical supplies,<br/>
              <span className="text-[#638971]">delivered with care.</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#595959] max-w-xl">
              From daily wellness essentials to complete home-care kits — shop clinically vetted supplies, shipped fast and handled with hospital-grade standards.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/products" data-testid="hero-shop-btn">
                <Button size="lg" className="rounded-full bg-[#638971] hover:bg-[#4F705B] text-white px-7">
                  Shop All Supplies <ArrowRight size={18} className="ml-2"/>
                </Button>
              </Link>
              <Link to="/products?category=First Aid" data-testid="hero-firstaid-btn">
                <Button size="lg" variant="outline" className="rounded-full border-[#2C4035] text-[#2C4035] hover:bg-[#2C4035] hover:text-white px-7">
                  First Aid Essentials
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <div className="text-center">
                <div className="font-heading text-2xl font-bold text-[#2C4035]">FDA</div>
                <div className="text-xs text-[#595959] mt-1">Compliant sourcing</div>
              </div>
              <div className="text-center border-x border-gray-200">
                <div className="font-heading text-2xl font-bold text-[#2C4035]">Miami</div>
                <div className="text-xs text-[#595959] mt-1">Ships from FL, USA</div>
              </div>
              <div className="text-center">
                <div className="font-heading text-2xl font-bold text-[#2C4035]">Ships</div>
                <div className="text-xs text-[#595959] mt-1">Rates by state & country</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-[#E8EDE9] blur-3xl opacity-70 -z-10"/>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-[#E8EDE9]">
              <img src="https://images.unsplash.com/photo-1603398938378-e54eab446dde?w=1600&h=1400&fit=crop&q=85"
                   alt="Assortment of medical supplies"
                   data-testid="hero-image"
                   className="w-full h-[520px] object-cover"/>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-lg max-w-[220px] border border-gray-100">
              <HeartPulse className="text-[#E48967]" size={22}/>
              <div className="text-sm font-semibold text-[#2C4035] mt-2">Certified Quality</div>
              <div className="text-xs text-[#595959] mt-1">Every product vetted by our medical panel.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-white border-y border-gray-100">
        <div className="container-app px-6 md:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Truck, label: "Shipping varies by state & country" },
            { icon: ShieldCheck, label: "Secure Stripe checkout" },
            { icon: HeartPulse, label: "Medical-grade products" },
            { icon: Star, label: "Rated 4.9 by 12K+ patients" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F1F4F1] grid place-items-center">
                <f.icon size={18} className="text-[#638971]"/>
              </div>
              <span className="text-sm text-[#2C4035] font-medium">{f.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="section-pad">
        <div className="container-app">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A]">Shop by Category</h2>
              <p className="text-[#595959] mt-2">Everything you need for a well-equipped home.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {CATEGORIES.map((c) => (
              <Link key={c.name} to={`/products?category=${encodeURIComponent(c.name)}`}
                    data-testid={`category-${c.name.toLowerCase().replace(' ', '-')}`}
                    className="group relative rounded-2xl overflow-hidden border border-gray-100 bg-white product-card">
                <img src={c.img} alt={c.name} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500"/>
                <div className="p-4">
                  <div className="font-heading font-semibold text-[#2C4035]">{c.name}</div>
                  <div className="text-xs text-[#638971] mt-1 flex items-center gap-1">
                    Shop now <ArrowRight size={12}/>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="section-pad bg-white">
        <div className="container-app">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A]">Best sellers</h2>
              <p className="text-[#595959] mt-2">Loved by thousands of home caregivers.</p>
            </div>
            <Link to="/products" data-testid="view-all-featured" className="text-sm font-semibold text-[#638971] hover:text-[#4F705B]">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p}/>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad">
        <div className="container-app">
          <div className="relative rounded-3xl overflow-hidden bg-[#2C4035] p-10 md:p-16 text-white grain-bg">
            <div className="relative z-10 max-w-2xl">
              <h2 className="font-heading text-3xl md:text-5xl font-bold leading-tight">
                Care that arrives before you need it.
              </h2>
              <p className="mt-4 text-white/80 text-lg">Join 100,000+ households who keep B1 Medical Supply in their home for every emergency and everyday care moment.</p>
              <Link to="/products" data-testid="cta-shop-btn">
                <Button size="lg" className="mt-7 rounded-full bg-[#E48967] hover:bg-[#c96f4f] text-white px-8">
                  Start Shopping <ArrowRight size={18} className="ml-2"/>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
